import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, of, Subscriber, Subscription } from 'rxjs';
import { PendingChangesDialogComponent } from "../components/pending-changes-dialog/pending-changes-dialog.component";

@Injectable({
  providedIn: 'root',
})
export class HasPendingChangesGuard implements OnDestroy {
  public readonly SAVE_CHANGES = 1;
  public readonly DISCARD_CHANGES = 2;

  private changes: any = {};
  private subscriptions: Subscription[] = [];
  private router: Router;
  private dialog: MatDialog;

  constructor(router: Router, dialog: MatDialog) {
    this.router = router;
    this.dialog = dialog;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.hasPendingChanges(state.url);
  }

  canDeactivate(
    component: any,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    if (!nextState) return of(true);
    return this.hasPendingChanges(nextState.url);
  }

  registerChanges(onSaveClicked: any, onDiscardClicked: any = null, url: string | null = null): void {
    if (!url) {
      url = this.router.url;
    }
    this.changes[url] = {
      onSaveClicked,
      onDiscardClicked,
      warned: false,
      nextUrl: null,
    };
  }

  unregisterChanges(url: string | null = null): void {
    if (!url) {
      url = this.router.url;
    }
    delete this.changes[url];
  }

  hasChanges(url: string | null = null): boolean {
    if (!url) {
      url = this.router.url;
    }
    return !!this.changes[url];
  }

  private hasPendingChanges(nextUrl: string): Observable<boolean | UrlTree> {
    return new Observable((subscriber: Subscriber<boolean | UrlTree>) => {
      const url = this.router.url;
      if (this.changes[url] && !this.changes[url].warned) {
        this.changes[url].nextUrl = nextUrl;
        this.warn(subscriber);
      } else {
        subscriber.next(true);
        subscriber.complete();
      }
    });
  }

  private warn(subscriber: Subscriber<boolean | UrlTree>): void {
    const dialogRef: MatDialogRef<any> = this.dialog.open(PendingChangesDialogComponent);
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((result) => {
        if (result === this.SAVE_CHANGES) {
          this.onSave(subscriber);
        } else if (result === this.DISCARD_CHANGES) {
          this.onDiscardChangesConfirmed(subscriber);
        } else {
          subscriber.next(false);
          subscriber.complete();
        }
      })
    );
  }

  private onDiscardChangesConfirmed(subscriber: Subscriber<any>): void {
    if (this.changes[this.router.url].onDiscardClicked) {
      this.changes[this.router.url].onDiscardClicked();
    } else {
      const nextUrl = this.changes[this.router.url].nextUrl;
      this.unregisterChanges(this.router.url);
      this.router.navigateByUrl(nextUrl).then(() => {
        subscriber.next(true);
        subscriber.complete();
      });
    }
  }

  private onSave(subscriber: Subscriber<any>): void {
    const nextUrl = this.changes[this.router.url].nextUrl;

    const onSaveClicked = this.changes[this.router.url].onSaveClicked();
    if (onSaveClicked) {
      onSaveClicked.then((succeed: boolean) => {
        if (succeed) {
          this.unregisterChanges(this.router.url);
          this.router.navigateByUrl(nextUrl).then(() => {
            subscriber.next(true);
            subscriber.complete();
          });
        } else {
          subscriber.next(false);
          subscriber.complete();
        }
      });
    }
  }
}
